import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { adminAPI, adminSession } from '@/lib/api';
import { DEV_FEDERATION_FAILED_KEY } from '@/lib/environments';
import { useIdleLogout } from '@/lib/useIdleLogout';
import { EnvBanner } from '@/components/EnvBanner';
import { EnvToggle } from '@/components/EnvToggle';
import { SidebarIdentity } from '@/components/SidebarIdentity';
import { NavIcon } from '@/components/NavIcon';
import { DashboardOverview } from './DashboardOverview';
import { UsersPage } from './Users';
import { UsersNewPage } from './UsersNew';
import { UserDetailPage } from './UserDetail';
import { ModerationPage } from './Moderation';
import { AuditLogPage } from './AuditLog';
import { DeveloperPage } from './Developer';
import { ApiDocsPage } from './ApiDocs';
import { HealthPage } from './Health';
import { FlagsPage } from './Flags';
import { PaymentsPage } from './Payments';
import { PromptsPage } from './Prompts';
import { CreatorsPage } from './Creators';
import { CreatorDetailPage } from './CreatorDetail';
import { SupportPage } from './Support';
import { BlogPage } from './Blog';
import { BlogEditorPage } from './BlogEditor';
import { AdminsPage } from './Admins';

export function DashboardPage() {
  const navigate = useNavigate();
  const me = adminSession.getMe();
  // Only full admins manage the admin pool (the listAdmins/createAdminAccount
  // backend is role-gated too — this just hides the nav + route).
  const isAdmin = me?.role === 'admin';

  // Set by SignIn when a fresh login authenticated fine against prod but
  // the dev federation call failed — the admin landed in prod via a real
  // page load (see SignIn.tsx's landOnDashboard) with no dev session.
  // That load is what rebuilds the Convex client / API_BASE for prod, but
  // it also discards React Router state, so the flag rides sessionStorage
  // instead. Read once and clear immediately so it survives exactly this
  // one mount, not a later reload/revisit of /dashboard.
  const [devFederationFailed] = useState<boolean>(() => {
    const flagged = sessionStorage.getItem(DEV_FEDERATION_FAILED_KEY) === '1';
    if (flagged) sessionStorage.removeItem(DEV_FEDERATION_FAILED_KEY);
    return flagged;
  });

  useEffect(() => {
    if (!adminSession.isValid()) navigate('/auth/signin');
  }, [navigate]);

  // Phone-width nav drawer (the sidebar is always visible on wider screens —
  // see .admin-sidebar in layout.css). Picking a destination closes it.
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setNavOpen(false), [pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  // Auto sign-out on idle (30 min) or absolute-lifetime expiry.
  // Best-effort server revoke, then clear local creds + bounce to login.
  //
  // 'session-lost' is a different trigger from 'idle'/'expired': it means
  // the ACTIVE environment's session died on its own (a heartbeat 401),
  // not that the user went away. heartbeatAll() already cleared just that
  // environment's local session — clearAll()/signout() here would also
  // wipe a still-valid session in the other environment, which is exactly
  // the bug this is fixing. Only a genuine idle timeout or absolute
  // expiry counts as "the user is leaving" and clears everything.
  const handleAutoLogout = useCallback((reason: 'idle' | 'expired' | 'session-lost') => {
    if (reason === 'session-lost') {
      navigate('/auth/signin', { replace: true });
      return;
    }
    // Explicit user-facing sign-out event (idle timeout / absolute expiry)
    // — clear every environment, not just the active one, and revoke both
    // server-side sessions. See adminSession.clearAll().
    adminAPI.signout().catch(() => null);
    adminSession.clearAll();
    navigate('/auth/signin', { replace: true });
  }, [navigate]);
  useIdleLogout(handleAutoLogout);

  const signout = async () => {
    // "Sign out" means leaving both environments, not just the active one.
    await adminAPI.signout().catch(() => null);
    adminSession.clearAll();
    navigate('/auth/signin');
  };

  return (
    // Full-viewport app shell: the banner sits at the top, and the sidebar +
    // main scroll INDEPENDENTLY inside the grid below (no page-level scroll).
    <div className="admin-frame">
      {/* Always visible at the top of the shell (the shell itself never
          scrolls), on every /dashboard/* route. */}
      <EnvBanner />
      {devFederationFailed && (
        <div
          role="alert"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 99,
            background: '#8a5a00',
            color: '#fff',
            padding: '8px 16px',
            fontSize: 12,
            textAlign: 'center',
          }}
        >
          Signed in to production, but couldn't establish a development session — switching to
          Development will require signing in again.
        </div>
      )}
      <header className="admin-topbar">
        <button
          type="button"
          className="admin-topbar-toggle"
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={navOpen}
          aria-controls="admin-sidebar"
          onClick={() => setNavOpen((o) => !o)}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
            {navOpen
              ? <><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></>
              : <><line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" /></>}
          </svg>
        </button>
        <Link to="/dashboard" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="serif" style={{ fontStyle: 'italic', fontSize: 20, fontWeight: 500 }}>wydm admin</span>
        </Link>
      </header>
      <div className="admin-shell">
      {navOpen && <div className="admin-nav-backdrop" onClick={() => setNavOpen(false)} />}
      <aside id="admin-sidebar" className="admin-sidebar" data-open={navOpen}>
        {/* The phone top bar already carries the wordmark. */}
        <Link to="/dashboard" className="hide-mobile" style={{ textDecoration: 'none', color: 'inherit', display: 'inline-block' }}>
          <div className="serif" style={{ fontStyle: 'italic', fontSize: 22, fontWeight: 500 }}>wydm admin</div>
        </Link>
        <div className="admin-sidebar-env">
          <EnvToggle />
        </div>
        <div style={{ marginTop: 16 }}>
          <SidebarIdentity onSignOut={signout} />
        </div>
        <nav style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Link to="/dashboard" style={navLink}><NavIcon name="dashboard" />Dashboard</Link>
          <Link to="/dashboard/users" style={navLink}><NavIcon name="users" />Users</Link>
          <Link to="/dashboard/moderation" style={navLink}><NavIcon name="moderation" />Moderation</Link>
          <Link to="/dashboard/support" style={navLink}><NavIcon name="support" />Support</Link>
          <Link to="/dashboard/blog" style={navLink}><NavIcon name="blog" />Blog</Link>
          <Link to="/dashboard/payments" style={navLink}><NavIcon name="payments" />Payments</Link>
          <Link to="/dashboard/flags" style={navLink}><NavIcon name="flags" />Feature flags</Link>
          <Link to="/dashboard/prompts" style={navLink}><NavIcon name="prompts" />Prompts</Link>
          <Link to="/dashboard/creators" style={navLink}><NavIcon name="creators" />Creators</Link>
          <Link to="/dashboard/health" style={navLink}><NavIcon name="health" />Health</Link>
          <Link to="/dashboard/audit" style={navLink}><NavIcon name="audit" />Audit</Link>
          <Link to="/dashboard/developer" style={navLink}><NavIcon name="flags" />Developer</Link>
          <Link to="/dashboard/api-docs" style={navLink}><NavIcon name="prompts" />API docs</Link>
          {isAdmin && <Link to="/dashboard/admins" style={navLink}><NavIcon name="admins" />Admins</Link>}
        </nav>
      </aside>
      <main className="admin-main">
        <Routes>
          <Route index element={<DashboardOverview />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users/new" element={<UsersNewPage />} />
          <Route path="users/:id" element={<UserDetailPage />} />
          <Route path="moderation" element={<ModerationPage />} />
          {/* DM reports moved into the Moderation page (a "DM reports" tab). Redirect old links. */}
          <Route path="dm-reports" element={<Navigate to="/dashboard/moderation" replace />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="blog" element={<BlogPage />} />
          {/* One route for "new" and ids, so saving a new post keeps the editor mounted. */}
          <Route path="blog/:id" element={<BlogEditorPage />} />
          <Route path="health" element={<HealthPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="flags" element={<FlagsPage />} />
          <Route path="prompts" element={<PromptsPage />} />
          <Route path="creators" element={<CreatorsPage />} />
          <Route path="creators/:id" element={<CreatorDetailPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="developer" element={<DeveloperPage />} />
          <Route path="api-docs" element={<ApiDocsPage />} />
          {isAdmin && <Route path="admins" element={<AdminsPage />} />}
        </Routes>
      </main>
      </div>
    </div>
  );
}

const navLink: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '8px 12px', borderRadius: 8, color: 'var(--ink-2)', fontSize: 14,
  fontWeight: 600, textDecoration: 'none',
};

